import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

const COOKIE_CONSENT_KEY = 'ukai-cookie-consent';

type CookiePreferences = {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  functionality: boolean;
};

type CookieConsentContextType = {
  preferences: CookiePreferences;
  showBanner: boolean;
  savePreferences: (newPreferences: Partial<CookiePreferences>) => void;
  acceptAll: () => void;
  rejectNonEssential: () => void;
  openSettings: () => void;
};

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};

type CookieConsentProviderProps = {
  children: ReactNode;
};

export const CookieConsentProvider = ({ children }: CookieConsentProviderProps) => {
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    analytics: false,
    marketing: false,
    functionality: false,
  });

  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const savedPreferences = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!savedPreferences) {
      setShowBanner(true);
    } else {
      setPreferences(JSON.parse(savedPreferences));
    }
  }, []);

  const savePreferences = (newPreferences: Partial<CookiePreferences>) => {
    const updatedPreferences = {
      ...preferences,
      ...newPreferences,
      essential: true,
    };
    setPreferences(updatedPreferences);
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(updatedPreferences));
    setShowBanner(false);
  };

  const acceptAll = () => {
    savePreferences({
      analytics: true,
      marketing: true,
      functionality: true,
    });
  };

  const rejectNonEssential = () => {
    savePreferences({
      analytics: false,
      marketing: false,
      functionality: false,
    });
  };

  const openSettings = () => {
    setShowBanner(true);
  };

  const value = {
    preferences,
    showBanner,
    savePreferences,
    acceptAll,
    rejectNonEssential,
    openSettings,
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};
